import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Clock, Facebook, Twitter, Instagram, Award } from 'lucide-react';
import { Button } from './ui/Button';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigation = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'Services',
    href: '/services'
  }, {
    name: 'Fleet',
    href: '/fleet'
  }, {
    name: 'Pricing',
    href: '/pricing'
  }, {
    name: 'Service Area',
    href: '/service-area'
  }, {
    name: 'About',
    href: '/about'
  }, {
    name: 'FAQ',
    href: '/faq'
  }];
  const isActive = (path: string) => location.pathname === path;
  return <div className="min-h-screen flex flex-col font-sans bg-white" data-id="element-34">
      {/* Top Utility Bar */}
      <div className="bg-black text-white py-2 px-4 text-sm hidden md:block border-b border-warm-800" data-id="element-35">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-36">
          <div className="flex items-center space-x-6" data-id="element-37">
            <a href="tel:5551234567" className="flex items-center hover:text-primary-600 transition-colors font-medium" data-id="element-38">
              <Phone className="w-4 h-4 mr-2" data-id="element-39" /> +1 (555) 123-4567
            </a>
            <a href="tel:5559876543" className="flex items-center hover:text-primary-600 transition-colors font-medium" data-id="element-40">
              <Phone className="w-4 h-4 mr-2" data-id="element-41" /> +1 (555) 987-6543
            </a>
          </div>
          <div className="flex items-center text-primary-600 font-semibold tracking-wide text-xs uppercase" data-id="element-42">
            <Award className="w-4 h-4 mr-2" data-id="element-43" />
            15+ Years of Experience in the Industry
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="bg-black sticky top-0 z-50" data-id="element-44">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-45">
          <div className="flex justify-between h-20" data-id="element-46">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0" data-id="element-47">
              <Link to="/" className="flex items-center group" data-id="element-48">
                <img src="https://cdn.magicpatterns.com/uploads/8cvtGHTpu2TP3oAB44TxH8/tmp_60f8a779-90fc-4093-967e-c02a439baa5d.png" alt="Gozie Transportation" className="h-14 w-auto" data-id="element-49" />
              </Link>
            </div>

            {/* Desktop Nav Links */}
            <nav className="hidden xl:flex items-center justify-center flex-1 px-8" data-id="element-50">
              <div className="flex space-x-1" data-id="element-51">
                {navigation.map(item => <Link key={item.name} to={item.href} className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${isActive(item.href) ? 'text-primary-600 bg-white/10' : 'text-white hover:text-primary-600'}`} data-id="element-52">
                    {item.name}
                  </Link>)}
              </div>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4 flex-shrink-0" data-id="element-53">
              <Link to="/contact" className="text-sm font-semibold text-white hover:text-primary-600 hidden xl:block" data-id="element-54">
                Contact
              </Link>
              <Link to="/booking" data-id="element-55">
                <Button variant="primary" data-id="element-56">Get a Quote</Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center xl:hidden space-x-4" data-id="element-57">
              <a href="tel:5551234567" className="text-primary-600 p-2 bg-white/10 rounded-full" data-id="element-58">
                <Phone className="w-5 h-5" data-id="element-59" />
              </a>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-primary-600 p-2 focus:outline-none" aria-expanded={isMobileMenuOpen} data-id="element-60">
                <span className="sr-only" data-id="element-61">Open main menu</span>
                {isMobileMenuOpen ? <X className="block h-7 w-7" data-id="element-62" /> : <Menu className="block h-7 w-7" data-id="element-63" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Drawer */}
        <div className={`xl:hidden absolute w-full bg-black border-t border-warm-800 shadow-xl transition-all duration-300 ease-in-out origin-top ${isMobileMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}`} data-id="element-64">
          <div className="px-4 pt-2 pb-6 space-y-1 max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-65">
            {navigation.map(item => <Link key={item.name} to={item.href} onClick={() => setIsMobileMenuOpen(false)} className={`block px-4 py-3 rounded-lg text-base font-semibold ${isActive(item.href) ? 'text-primary-600 bg-white/10' : 'text-white hover:text-primary-600 hover:bg-white/5'}`} data-id="element-66">
                {item.name}
              </Link>)}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className={`block px-4 py-3 rounded-lg text-base font-semibold ${isActive('/contact') ? 'text-primary-600 bg-white/10' : 'text-white hover:text-primary-600 hover:bg-white/5'}`} data-id="element-67">
              Contact
            </Link>
            <div className="pt-4 pb-2" data-id="element-68">
              <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)} data-id="element-69">
                <Button variant="primary" fullWidth size="lg" data-id="element-70">
                  Get a Quote Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow" data-id="element-71">{children}</main>

      {/* Footer */}
      <footer className="bg-black text-white" data-id="element-72">
        <div className="py-12" data-id="element-73">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-74">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-75">
              {/* Company Info */}
              <div className="space-y-6" data-id="element-76">
                <Link to="/" className="inline-block" data-id="element-77">
                  <img src="https://cdn.magicpatterns.com/uploads/8cvtGHTpu2TP3oAB44TxH8/tmp_60f8a779-90fc-4093-967e-c02a439baa5d.png" alt="Gozie Transportation" className="h-12 w-auto" data-id="element-78" />
                </Link>
                <p className="text-sm leading-relaxed text-warm-400" data-id="element-79">
                  Providing safe, reliable, and compassionate wheelchair
                  transportation and accessible taxi services for individuals
                  with mobility challenges across the metropolitan area.
                </p>
                <div className="flex space-x-4 pt-2" data-id="element-80">
                  <a href="#" className="w-10 h-10 rounded-full bg-warm-800 flex items-center justify-center text-warm-400 hover:bg-primary-600 hover:text-white transition-colors" data-id="element-81">
                    <Facebook className="w-5 h-5" data-id="element-82" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-warm-800 flex items-center justify-center text-warm-400 hover:bg-primary-600 hover:text-white transition-colors" data-id="element-83">
                    <Twitter className="w-5 h-5" data-id="element-84" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-warm-800 flex items-center justify-center text-warm-400 hover:bg-primary-600 hover:text-white transition-colors" data-id="element-85">
                    <Instagram className="w-5 h-5" data-id="element-86" />
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div data-id="element-87">
                <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-88">
                  Quick Links
                </h3>
                <ul className="space-y-3 text-sm font-medium" data-id="element-89">
                  <li data-id="element-90">
                    <Link to="/services" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-91">
                      Our Services
                    </Link>
                  </li>
                  <li data-id="element-92">
                    <Link to="/fleet" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-93">
                      Our Fleet
                    </Link>
                  </li>
                  <li data-id="element-94">
                    <Link to="/pricing" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-95">
                      Pricing &amp; Insurance
                    </Link>
                  </li>
                  <li data-id="element-96">
                    <Link to="/service-area" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-97">
                      Service Area
                    </Link>
                  </li>
                  <li data-id="element-98">
                    <Link to="/booking" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-99">
                      Book a Ride
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Resources */}
              <div data-id="element-100">
                <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-101">
                  Resources
                </h3>
                <ul className="space-y-3 text-sm font-medium" data-id="element-102">
                  <li data-id="element-103">
                    <Link to="/about" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-104">
                      About Us
                    </Link>
                  </li>
                  <li data-id="element-105">
                    <Link to="/faq" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-106">
                      FAQ
                    </Link>
                  </li>
                  <li data-id="element-107">
                    <Link to="/contact" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-108">
                      Contact Us
                    </Link>
                  </li>
                  <li data-id="element-109">
                    <Link to="/privacy-policy" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-110">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact */}
              <div data-id="element-111">
                <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-112">
                  Contact Info
                </h3>
                <ul className="space-y-4 text-sm" data-id="element-113">
                  <li className="flex items-start" data-id="element-114">
                    <Phone className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-115" />
                    <div data-id="element-116">
                      <a href="tel:5551234567" className="text-white font-bold hover:text-primary-600 transition-colors block" data-id="element-117">
                        +1 (555) 123-4567
                      </a>
                      <span className="text-xs text-warm-500 mt-1 block" data-id="element-118">
                        Available 24/7 for dispatch
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start" data-id="element-119">
                    <MapPin className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-120" />
                    <span className="text-warm-400 leading-relaxed" data-id="element-121">
                      123 Mobility Way
                      <br data-id="element-122" />
                      Suite 100
                      <br data-id="element-123" />
                      Metro City, ST 12345
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-124">
                    <Clock className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-125" />
                    <span className="text-warm-400 leading-relaxed" data-id="element-126">
                      Office: Mon-Fri 8am-5pm
                      <br data-id="element-127" />
                      Rides: 24/7/365
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-warm-800 py-6 text-center text-sm text-warm-500" data-id="element-128">
          <div className="max-w-7xl mx-auto px-4" data-id="element-129">
            <p data-id="element-130">
              &copy; {new Date().getFullYear()} Gozie Transportation. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}